import {
  GenericDataModel,
  MemberInvitedAcceptedNotificationDataModel,
  MemberInviteReceivedNotificationDataModel,
  NewGoogleReviewConvertedDataModel,
  NewReviewOpportunityDataModel,
  NewReviewReceivedDataModel,
  SubscriptionRenewalDataModel,
  UserNotificationData,
  UserNotificationType,
} from '@rma/dashboard/notification-bell/private/api-user-notifications';
import { FINANCE, LEASING, SALES } from '@rma/generic/domain/types';
import { format } from 'date-fns';
import { NotificationMetaData } from './notification-meta-data';

function GetGenericMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as GenericDataModel;
  return {
    mergeData: {
      subject: d.Subject,
      cta: d.CallToActionText,
    },
    agentRoute: { path: d.RedirectUrl },
    agencyRoute: { path: d.RedirectUrl },
  };
}

function GetNewReviewOpportunityMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as NewReviewOpportunityDataModel;
  return {
    mergeData: {
      address: d.PropertyAddress,
    },
    agentRoute: {
      path: '/reviews/request/request-review',
      params: { campaignCode: d.CampaignCode },
    },
    agencyRoute: { path: '/reviews/request/opportunities', params: {} },
  };
}

function GetNewGoogleReviewConvertedMetaData(data: UserNotificationData): NotificationMetaData {
  // eslint-disable-next-line no-restricted-syntax -- alone among the notification payloads, NewGoogleReviewConvertedDataModel was never added to the UserNotificationData union, so it overlaps no member and a direct assertion will not compile.
  const d = data as unknown as NewGoogleReviewConvertedDataModel;
  return {
    mergeData: {
      reviewerName: d.ReviewerName,
      address: d.PropertyAddress,
    },
    agentRoute: { path: '/google-profile/google-reviews/manage' },
    agencyRoute: { path: '/google-profile/google-reviews/manage' },
  };
}

function GetNewReviewReceivedMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as NewReviewReceivedDataModel;
  let verticalType = d.VerticalType;
  if (!Number.isNaN(parseInt(verticalType, 10))) {
    // HW. 24/07/2023. Vertical type not serialised to string, this is fixed in the backend; but existing messages may still be this way.
    // Can remove after 3 months, as the data shouls be correct in future.
    switch (parseInt(verticalType, 10)) {
      case 2:
        verticalType = LEASING.toString();
        break;
      case 3:
        verticalType = FINANCE.toString();
        break;
      default:
        verticalType = SALES.toString();
        break;
    }
  }

  return {
    mergeData: {
      reviewerName: d.ReviewerName,
      address: d.PropertyAddress,
      verticalType: verticalType,
      stars: d.Stars.toString(),
    },
    agentRoute: {
      path: `/reviews/completed/${d.ReviewCode}/received`,
    },
    agencyRoute: {
      path: `/reviews/completed/${d.ReviewCode}/received`,
    },
  };
}

function GetSubscriptionRenewalMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as SubscriptionRenewalDataModel;

  return {
    mergeData: {
      productName: d.ProductName,
      date: format(new Date(d.NextPaymentAttempt ?? d.DueDate), 'dd MMM yyyy'),
    },
    agentRoute: { path: '/billing/my-subscription' },
    agencyRoute: { path: '/billing/my-subscription' },
  };
}

function GetMemberInviteAcceptedMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as MemberInvitedAcceptedNotificationDataModel;

  return {
    mergeData: {
      memberName: d.MemberName,
      invitedToProfileName: d.InvitedToProfileName,
      invitedByName: d.InvitedByName,
    },
    agentRoute: { path: '' },
    agencyRoute: { path: '/manage-account/team-members' },
  };
}

function GetMemberInviteReceivedMetaData(data: UserNotificationData): NotificationMetaData {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const d = data as MemberInviteReceivedNotificationDataModel;

  return {
    mergeData: {
      memberName: d.MemberName,
      invitedToProfileName: d.InvitedToProfileName,
      invitedByName: d.InvitedByName,
    },
    agentRoute: { path: '' },
    agencyRoute: { path: '' },
    redirectUrl: d.AcceptUrl,
  };
}

export const metaDataMap = {
  [UserNotificationType.Generic]: GetGenericMetaData,
  [UserNotificationType.NewReviewOpportunity]: GetNewReviewOpportunityMetaData,
  [UserNotificationType.NewGoogleReviewConverted]: GetNewGoogleReviewConvertedMetaData,
  [UserNotificationType.NewReviewReceived]: GetNewReviewReceivedMetaData,
  [UserNotificationType.SubscriptionRenewal]: GetSubscriptionRenewalMetaData,
  [UserNotificationType.MemberInviteAccepted]: GetMemberInviteAcceptedMetaData,
  [UserNotificationType.MemberInviteReceived]: GetMemberInviteReceivedMetaData,
};
